import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  HardDrive,
  CalendarClock,
  FolderOpen,
  ClipboardList,
  LayoutGrid,
  ListChecks,
  LogOut,
  Map as MapIcon,
  Megaphone,
  Menu,
  MessageSquare,
  Plug,
  Settings,
  ShieldCheck,
  Sun,
  Users,
  UsersRound,
  X,
} from 'lucide-react';
import type { Permission } from '@homework/shared';
import { useAuth, useMe } from '../lib/auth';
import { useRealtime } from '../lib/realtime';
import { Avatar, cx } from './ui';
import { SessionTimer } from './SessionTimer';
import { StatusSwitcher } from './StatusSwitcher';
import { NotificationsMenu } from './NotificationsMenu';
import { KnockPrompts } from './KnockPrompts';
import { CallOverlay } from './CallOverlay';
import { CompanionWidget } from './companion/CompanionWidget';
import { CompanionProvider } from '../lib/companion';
import { MeetingNotesProvider } from '../lib/meetingNotes';
import { MeetingNotesLayer } from './MeetingNotesLayer';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  /** Shown when the viewer holds any one of these. */
  permission?: Permission | Permission[];
  section: 'me' | 'team' | 'admin';
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/office', label: 'Office', icon: MapIcon, section: 'me' },
  { to: '/directory', label: "Who's around", icon: Users, section: 'me' },
  { to: '/my-day', label: 'My day', icon: Sun, section: 'me' },
  { to: '/tasks', label: 'My tasks', icon: ListChecks, section: 'me' },
  { to: '/chat', label: 'Chat', icon: MessageSquare, section: 'me' },
  { to: '/files', label: 'Files', icon: FolderOpen, section: 'me' },
  { to: '/leave', label: 'Leave', icon: CalendarClock, section: 'me' },
  { to: '/announcements', label: 'Announcements', icon: Megaphone, section: 'me' },

  { to: '/team', label: 'My team', icon: UsersRound, permission: 'team:read', section: 'team' },
  { to: '/team/leave', label: 'Approvals', icon: ClipboardList, permission: 'leave:approve:team', section: 'team' },
  { to: '/hr', label: 'Live board', icon: LayoutGrid, permission: 'report:read:org', section: 'team' },
  { to: '/hr/reports', label: 'Attendance', icon: ClipboardList, permission: 'report:read:org', section: 'team' },

  { to: '/admin/users', label: 'People & roles', icon: Users, permission: 'user:manage', section: 'admin' },
  { to: '/admin/map', label: 'Office layout', icon: MapIcon, permission: 'map:manage', section: 'admin' },
  {
    to: '/admin/settings',
    label: 'Settings',
    icon: Settings,
    // HR reaches this screen for leave policy; Admin for everything else.
    permission: ['integration:manage', 'leave:policy:manage'],
    section: 'admin',
  },
  { to: '/admin/storage', label: 'File storage', icon: HardDrive, permission: 'user:manage', section: 'admin' },
  { to: '/admin/audit', label: 'Audit log', icon: ShieldCheck, permission: 'audit:read', section: 'admin' },
];

const SECTION_LABELS: Record<NavItem['section'], string> = {
  me: 'My office',
  team: 'Team & people',
  admin: 'Administration',
};

export function AppShell() {
  const me = useMe();
  const { can, logout } = useAuth();
  const { connected, presence } = useRealtime();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer whenever navigation happens.
  useEffect(() => setMobileNavOpen(false), [location.pathname]);

  const onlineCount = [...presence.values()].filter((user) => user.status !== 'OFFLINE').length;
  const sections: Array<NavItem['section']> = ['me', 'team', 'admin'];

  const navFor = (section: NavItem['section']) =>
    NAV.filter((item) => item.section === section).filter((item) => {
      if (!item.permission) return true;
      return Array.isArray(item.permission) ? item.permission.some(can) : can(item.permission);
    });

  const sidebar = (
    <nav className="flex h-full flex-col gap-6 overflow-y-auto p-4" aria-label="Main navigation">
      <Link to="/office" className="flex items-center gap-2.5 px-2">
        <span className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-brand-400 via-brand-500 to-violet-500 font-bold text-white shadow-vivid-sm">
          H
        </span>
        <span>
          <span className="block font-bold leading-tight text-slate-900">HomeWork</span>
          <span className="block text-xs text-slate-500">
            {onlineCount} {onlineCount === 1 ? 'person' : 'people'} in the office
          </span>
        </span>
      </Link>

      {sections.map((section) => {
        const items = navFor(section);
        if (items.length === 0) return null;
        return (
          <div key={section}>
            <p className="px-3 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              {SECTION_LABELS[section]}
            </p>
            <ul className="space-y-0.5">
              {items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cx(
                        'flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold transition-all duration-200',
                        isActive
                          ? 'bg-gradient-to-r from-brand-500 to-violet-500 text-white shadow-vivid-sm'
                          : 'text-slate-600 hover:bg-white/60 hover:text-slate-900',
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <div className="mt-auto space-y-1">
        <NavLink
          to="/settings/integrations"
          className={({ isActive }) =>
            cx(
              'flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold transition-all duration-200',
              isActive
                ? 'bg-gradient-to-r from-brand-500 to-violet-500 text-white shadow-vivid-sm'
                : 'text-slate-600 hover:bg-white/60 hover:text-slate-900',
            )
          }
        >
          <Plug className="h-4 w-4" aria-hidden />
          Integrations
        </NavLink>
        <Link
          to={`/people/${me.user.id}`}
          className="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm text-slate-600 transition hover:bg-white/60"
        >
          <Avatar name={me.user.name} photoUrl={me.user.photoUrl} seed={me.user.id} size="sm" />
          <span className="min-w-0">
            <span className="block truncate font-semibold text-slate-800">{me.user.name}</span>
            <span className="block truncate text-xs text-slate-500">
              {me.user.roles.map((role) => role.charAt(0) + role.slice(1).toLowerCase()).join(' · ')}
            </span>
          </span>
        </Link>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-white/60"
          onClick={() => void logout()}
        >
          <LogOut className="h-4 w-4" aria-hidden />
          Sign out
        </button>
      </div>
    </nav>
  );

  return (
    <CompanionProvider>
      <MeetingNotesProvider>
        <div className="flex h-full">
          <a href="#main" className="sr-only-focusable btn-primary absolute left-4 top-4 z-50">
            Skip to content
          </a>

          <aside className="hidden shrink-0 p-3 pr-0 lg:block">
            <div className="glass h-full w-64 overflow-hidden rounded-4xl">{sidebar}</div>
          </aside>

          {mobileNavOpen ? (
            <div className="fixed inset-0 z-40 lg:hidden">
              <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileNavOpen(false)} aria-hidden />
              <aside className="glass-strong relative h-full w-72 animate-slide-up rounded-r-4xl">
                <button
                  type="button"
                  className="btn-ghost absolute right-2 top-2 p-2"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label="Close navigation"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
                {sidebar}
              </aside>
            </div>
          ) : null}

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="glass sticky top-0 z-30 m-3 flex items-center gap-3 rounded-full px-3 py-2">
              <button
                type="button"
                className="btn-ghost p-2 lg:hidden"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" aria-hidden />
              </button>

              <SessionTimer />

              <span
                className={cx(
                  'badge ml-auto',
                  connected
                    ? 'bg-emerald-400/20 text-emerald-800 ring-1 ring-inset ring-emerald-500/25'
                    : 'bg-amber-400/25 text-amber-800 ring-1 ring-inset ring-amber-500/30',
                )}
                title={connected ? 'Live connection is healthy' : 'Reconnecting to the office…'}
              >
                <span
                  className={cx(
                    'h-2 w-2 rounded-full',
                    connected ? 'bg-emerald-500' : 'animate-pulse bg-amber-500',
                  )}
                  aria-hidden
                />
                {connected ? 'Live' : 'Reconnecting'}
              </span>

              <NotificationsMenu />

              <StatusSwitcher />
            </header>

            <main id="main" className="min-w-0 flex-1 overflow-y-auto px-3 pb-3">
              <Outlet />
            </main>
          </div>

          {/* Interruption requests and live calls follow the user across every screen. */}
          <KnockPrompts />
          <CallOverlay />
          {/* The personal companion, one click away on every screen. */}
          <CompanionWidget />
          {/* Someone's Mochi taking notes in the room you are in. */}
          <MeetingNotesLayer />
        </div>
      </MeetingNotesProvider>
    </CompanionProvider>
  );
}
